import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "../../../services/auth.service";
import { ScreenSizeService } from "src/app/services/screen-size.service";
import {
  BiometricAuthService,
  BiometricAvailability,
} from "../../../services/biometric-auth.service";
import { Preferences } from "@capacitor/preferences";
import { BiometryErrorType } from "@aparajita/capacitor-biometric-auth";
import { Keyboard } from "@capacitor/keyboard";
import { Capacitor } from "@capacitor/core";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
})
export class LoginComponent implements OnInit {
  username: string = "";
  password: string = "";
  email: string = "";
  loginError: string = "";
  resetSuccess: boolean = false;
  resetMode: boolean = false; // Determines whether we're in reset password mode
  resetLoading: boolean = false;

  isMobile: boolean;
  isDesktop: boolean;

  biometricAvailable: boolean = false;
  biometricType: string = "Biometric";
  storedUsername: string = "";
  biometricAuthInProgress: boolean = false;
  biometricNotEnrolled: boolean = false;
  biometricSetupMessage: string = "";

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private screenSizeService: ScreenSizeService,
    private biometricAuthService: BiometricAuthService
  ) {}

  async ngOnInit(): Promise<void> {
    document.body.classList.add("login-background");

    // Pre-fill username when arriving here to switch into a linked account
    const prefillUsername = this.route.snapshot.queryParamMap.get("username");
    if (prefillUsername) {
      this.username = prefillUsername;
    }

    // Handle keyboard events on native platforms to prevent layout shift
    if (Capacitor.isNativePlatform()) {
      Keyboard.addListener("keyboardWillShow", (info) => {
        // Prevent default behavior that causes black stripe
        document.body.style.height = "100vh";
        document.body.style.overflow = "hidden";
      });

      Keyboard.addListener("keyboardWillHide", () => {
        // Reset body styles
        document.body.style.height = "";
        document.body.style.overflow = "";
      });
    }

    this.screenSizeService.isMobile$.subscribe((isMobile) => {
      this.isMobile = isMobile;
    });
    this.screenSizeService.isDesktop$.subscribe((isDesktop) => {
      console.log("IsDeskTop: " + isDesktop);
      this.isDesktop = isDesktop;
    });

    // Defensive: Check if user is already logged in with valid token
    // If not, ensure all auth state is cleared to prevent inconsistent state
    const isLoggedIn = await this.authService.isLoggedIn();
    if (isLoggedIn) {
      // User has valid token, redirect to home
      this.router.navigate(["/home"]);
      return;
    }

    // Check biometric availability
    await this.checkBiometricAvailability();

    // Load stored credentials if available
    await this.loadStoredCredentials();
  }

  async checkBiometricAvailability(): Promise<void> {
    const availability: BiometricAvailability =
      await this.biometricAuthService.checkBiometricAvailability();
    this.biometricAvailable = availability.isAvailable;
    console.log("Biometric availability check:", JSON.stringify(availability));

    if (availability.isAvailable) {
      // Always use "Face ID" as the generic term for biometric authentication
      this.biometricType = "Face ID";
    } else if (
      availability.reason &&
      availability.reason.includes("not have any biometrics enrolled")
    ) {
      // Device supports biometrics but user hasn't enrolled
      this.biometricNotEnrolled = true;
      this.biometricSetupMessage = `Enable face recognition in your device settings for faster login.`;
    }
  }

  async loadStoredCredentials(): Promise<void> {
    try {
      const { value: username } = await Preferences.get({
        key: "biometric_username",
      });
      const { value: hasPassword } = await Preferences.get({
        key: "biometric_has_password",
      });

      console.log(
        "Stored credentials check:",
        JSON.stringify({
          username: username,
          hasPassword: hasPassword,
          willShowButton: !!(username && hasPassword === "true"),
        })
      );

      if (username && hasPassword === "true") {
        this.storedUsername = username;
      }
    } catch (error) {
      console.error("Error loading stored credentials:", error);
    }
  }

  async authenticateWithBiometric(): Promise<void> {
    if (!this.biometricAvailable || !this.storedUsername) {
      return;
    }

    this.biometricAuthInProgress = true;
    this.loginError = "";

    try {
      const result = await this.biometricAuthService.authenticate(
        `Log in as ${this.storedUsername}`
      );

      if (result.success) {
        // Retrieve stored password
        const { value: encryptedPassword } = await Preferences.get({
          key: "biometric_password",
        });

        if (encryptedPassword) {
          // Attempt login with stored credentials
          await this.authService.login(this.storedUsername, encryptedPassword);
          this.router.navigate(["/home"]);
        } else {
          this.loginError =
            "Stored credentials not found. Please log in manually.";
        }
      } else {
        // Handle biometric auth failure
        if (result.errorCode !== BiometryErrorType.userCancel) {
          this.loginError = result.error || "Biometric authentication failed";
        }
      }
    } catch (error) {
      console.error("Biometric login failed", error);
      const msg =
        (error as any)?.error?.detail ||
        (error as any)?.error?.message ||
        (error as any)?.error ||
        (error as any)?.message ||
        "Login failed. Please check your credentials and try again.";
      this.loginError = typeof msg === "string" ? msg : JSON.stringify(msg);
    } finally {
      this.biometricAuthInProgress = false;
    }
  }

  async storeCredentialsForBiometric(
    username: string,
    password: string
  ): Promise<void> {
    try {
      await Preferences.set({ key: "biometric_username", value: username });
      await Preferences.set({ key: "biometric_password", value: password });
      await Preferences.set({ key: "biometric_has_password", value: "true" });
    } catch (error) {
      console.error("Error storing credentials:", error);
    }
  }

  async clearStoredCredentials(): Promise<void> {
    try {
      await Preferences.remove({ key: "biometric_username" });
      await Preferences.remove({ key: "biometric_password" });
      await Preferences.remove({ key: "biometric_has_password" });
      this.storedUsername = "";
    } catch (error) {
      console.error("Error clearing credentials:", error);
    }
  }

  ngOnDestroy(): void {
    document.body.classList.remove("login-background");

    // Remove keyboard listeners
    if (Capacitor.isNativePlatform()) {
      Keyboard.removeAllListeners();
    }
  }

  // Modify onSubmit to use async/await for login
  async onSubmit(): Promise<void> {
    try {
      await this.authService.login(this.username, this.password); // Await the login promise

      // If biometric is available, offer to store credentials
      if (this.biometricAvailable) {
        await this.storeCredentialsForBiometric(this.username, this.password);
      }

      this.router.navigate(["/home"]);
    } catch (error) {
      console.error("Login failed", error);
      const msg =
        (error as any)?.error?.detail ||
        (error as any)?.error?.message ||
        (error as any)?.error ||
        (error as any)?.message ||
        "Login failed. Please check your credentials and try again.";
      this.loginError = typeof msg === "string" ? msg : JSON.stringify(msg);
    }
  }

  // Modify onResetSubmit to handle async resetPassword method
  async onResetSubmit(): Promise<void> {
    try {
      this.resetLoading = true;
      await this.authService.resetPassword(this.email); // Await the resetPassword promise
      this.resetSuccess = true;
      this.resetMode = false;
      this.email = "";
    } catch (error) {
      this.loginError = "Failed to submit reset password request.";
      console.error("Reset password failed", error);
    } finally {
      this.resetLoading = false;
    }
  }

  passwordVisible: boolean = false;
  togglePasswordVisibility() {
    this.passwordVisible = !this.passwordVisible;
  }

  switchToReset() {
    this.resetMode = true;
    this.loginError = "";
    this.resetSuccess = false;
  }

  switchToLogin() {
    this.resetMode = false;
    this.loginError = "";
    this.resetSuccess = false;
  }
}
